// /about/team/$slug — Individual author/attorney page with deep Person + LegalService schema.
// Maximum E-E-A-T: per-attorney bio, credentials, license number, specialties.
// AI engines (ChatGPT, Perplexity, Gemini) cite Person schema for "who is the
// notary public at X?" / "expert in Thai notarization" queries.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ATTORNEYS, getAttorneyBySlug } from "@/data/attorneys";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { BRAND_NAME, BRAND_URL, BRAND_PHONE } from "@/lib/brand";

export const Route = createFileRoute("/about/team/$slug")({
  head: ({ params }) => {
    const a = getAttorneyBySlug(params.slug);
    if (!a) return { meta: [{ title: ctaTitle("ไม่พบทนายความ", "/about/team/$slug", "th")}] };
    const path = `/about/team/${a.slug}`;
    const url = `${BRAND_URL}${path}`;
    const title = `${a.nameTh} (${a.nameEn}) — ${a.role} | ${BRAND_NAME}`;
    const desc = `ประวัติ ${a.nameTh} ${a.role} ใบอนุญาตทนายความเลขที่ ${a.license} (ตั้งแต่ปี ${a.licenseYear}) ประสบการณ์ ${a.yearsExp} ปี เชี่ยวชาญ ${a.specialties.join(", ")} — ${BRAND_NAME} — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(title, "/about/team/$slug", "th") },
        { name: "description", content: ctaDesc(desc, "/about/team/$slug", "th")},
        { property: "og:title", content: ctaTitle(title, "/about/team/$slug", "th")},
        { property: "og:description", content: ctaDesc(desc, "/about/team/$slug", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "profile" },
        { property: "og:image", content: `${BRAND_URL}${a.image}` },
        { property: "profile:first_name", content: a.nameEn.replace(/^(Mr\.|Miss|Mrs\.)\s*/, "").split(" ")[0] },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: canonicalAndHreflang(path),
      scripts: [
        consolidateGraph([
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Person",
                  "@id": `${url}#person`,
                  name: a.nameEn,
                  alternateName: a.nameTh,
                  jobTitle: a.roleEn,
                  description: a.bioEn,
                  url,
                  image: `${BRAND_URL}${a.image}`,
                  telephone: BRAND_PHONE,
                  knowsLanguage: a.languages,
                  knowsAbout: a.specialtiesEn,
                  hasCredential: [
                    {
                      "@type": "EducationalOccupationalCredential",
                      name: "Notarial Services Attorney",
                      credentialCategory: "license",
                      identifier: a.license,
                      datePublished: a.licenseYear,
                      recognizedBy: {
                        "@type": "Organization",
                        name: "Lawyers Council of Thailand",
                        url: "https://www.lawyerscouncil.or.th",
                      },
                    },
                    ...a.education.map((e) => ({
                      "@type": "EducationalOccupationalCredential",
                      name: e,
                      credentialCategory: "degree",
                    })),
                  ],
                  hasOccupation: {
                    "@type": "Occupation",
                    name: a.roleEn,
                    occupationalCategory: "23-1011 Lawyers",
                    experienceRequirements: `${a.yearsExp}+ years`,
                    skills: a.specialtiesEn.join(", "),
                  },
                  // P20 — Notable case studies as CreativeWork for E-E-A-T
                  subjectOf: a.cases.map((c, i) => ({
                    "@type": "CreativeWork",
                    "@id": `${url}#case-${i + 1}`,
                    name: c.titleEn,
                    about: c.category,
                    datePublished: c.year,
                    description: `${c.titleEn} — ${c.outcomeEn}`,
                    creator: { "@id": `${url}#person` },
                    inLanguage: "en",
                  })),
                  worksFor: { "@id": `${BRAND_URL}#organization` },
                  memberOf: {
                    "@type": "Organization",
                    name: "Lawyers Council of Thailand",
                    url: "https://www.lawyerscouncil.or.th",
                  },
                },
                {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    { "@type": "ListItem", position: 1, name: "หน้าแรก", item: BRAND_URL },
                    { "@type": "ListItem", position: 2, name: "ทีมงาน", item: `${BRAND_URL}/about/team` },
                    { "@type": "ListItem", position: 3, name: a.nameTh, item: url },
                  ],
                },
                {
                  "@type": "ProfilePage",
                  "@id": url,
                  url,
                  name: `${a.nameTh} — ${a.role}`,
                  mainEntity: { "@id": `${url}#person` },
                  about: { "@id": `${url}#person` },
                  inLanguage: "th",
                  dateModified: new Date().toISOString().slice(0, 10),
                  speakable: {
                    "@type": "SpeakableSpecification",
                    cssSelector: [".aeo-speakable", "h1", "h2"],
                  },
                },
              ],
            }),
          },
        ]),
      ],

    };
  },
  loader: ({ params }) => {
    const attorney = getAttorneyBySlug(params.slug);
    if (!attorney) throw notFound();
    return { attorney };
  },
  component: AuthorPage,
  notFoundComponent: () => (
    <div className="container mx-auto p-12 text-center">
      <h1 className="text-2xl font-bold">ไม่พบทนายความท่านนี้</h1>
      <Link to="/about/team" className="text-primary underline mt-4 inline-block">
        ← กลับไปหน้าทีมงาน
      </Link>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="container mx-auto p-12 text-center">
      <h1 className="text-2xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="text-muted-foreground mt-2">{error.message}</p>
      <button onClick={reset} className="mt-4 text-primary underline">
        ลองใหม่
      </button>
    </div>
  ),
});

function AuthorPage() {
  const { attorney: a } = Route.useLoaderData() as any;
  const related = ATTORNEYS.filter((x) => x.slug !== a.slug).slice(0, 5);

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">หน้าแรก</Link>
        <span className="mx-2">/</span>
        <Link to="/about/team" className="hover:text-primary">ทีมงาน</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{a.nameTh}</span>
      </nav>

      <header className="grid md:grid-cols-[240px_1fr] gap-8 mb-12">
        <img
          src={a.image}
          alt={`${a.nameTh} ${a.nameEn} — ${a.roleEn} ใบอนุญาตเลขที่ ${a.license}`}
          width={240}
          height={300}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="w-full rounded-2xl border shadow-elegant object-cover aspect-[4/5]"
        />
        <div>
          <p className="text-sm font-medium text-primary mb-2">{a.role}</p>
          <h1 className="text-4xl font-bold mb-2">{a.nameTh}</h1>
          <p className="text-xl text-muted-foreground mb-4">{a.nameEn}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">
              ใบอนุญาต {a.license}
            </span>
            <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">
              ตั้งแต่ปี {a.licenseYear}
            </span>
            <span className="px-3 py-1 rounded-full bg-secondary text-sm font-medium">
              ประสบการณ์ {a.yearsExp} ปี
            </span>
          </div>
          <p className="text-base leading-relaxed">{a.bio}</p>
        </div>
      </header>

      <section className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="rounded-2xl border p-6 bg-card">
          <h2 className="text-xl font-bold mb-4">ความเชี่ยวชาญ</h2>
          <ul className="space-y-2">
            {a.specialties.map((s: string) => (
              <li key={s} className="flex items-start gap-2">
                <span className="text-primary mt-1">▸</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border p-6 bg-card">
          <h2 className="text-xl font-bold mb-4">การศึกษาและใบอนุญาต</h2>
          <ul className="space-y-2">
            {a.education.map((e: string) => (
              <li key={e} className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span>{e}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            ภาษาที่สื่อสารได้: {a.languages.join(", ")}
          </p>
        </div>
      </section>

      <section className="rounded-2xl border p-6 bg-gradient-to-br from-primary/5 to-transparent mb-12">
        <h2 className="text-xl font-bold mb-3">About {a.nameEn} (English)</h2>
        <p className="leading-relaxed">{a.bioEn}</p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-2">ผลงานที่ผ่านมา (Notable Cases)</h2>
        <p className="text-sm text-muted-foreground mb-6">
          ตัวอย่างเคสจริงที่ {a.nameTh} เป็นผู้รับผิดชอบ — ข้อมูลถูกปรับให้ไม่ระบุชื่อลูกค้าเพื่อรักษาความลับ
        </p>
        <ol className="grid md:grid-cols-2 gap-4">
          {a.cases.map((c: typeof a.cases[number], i: number) => (
            <li
              key={i}
              className="rounded-2xl border p-5 bg-card hover:shadow-elegant transition-shadow"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold">
                  {c.year}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-secondary text-xs font-medium">
                  {c.category}
                </span>
              </div>
              <h3 className="font-semibold leading-snug mb-2">{c.title}</h3>
              <p className="text-sm text-muted-foreground mb-2">
                <span className="font-medium text-foreground">ผลลัพธ์:</span> {c.outcome}
              </p>
              <p className="text-xs text-muted-foreground italic border-t pt-2 mt-2">
                EN: {c.titleEn} — {c.outcomeEn}
              </p>
            </li>
          ))}
        </ol>
      </section>


      <section className="rounded-2xl border p-8 bg-card text-center mb-12">
        <h2 className="text-2xl font-bold mb-3">ปรึกษา {a.nameTh} โดยตรง</h2>
        <p className="text-muted-foreground mb-6">
          นัดหมายเพื่อใช้บริการ Notary Public หรือปรึกษากฎหมายโดย {a.role}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/quote"
            className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90"
          >
            ขอใบเสนอราคา
          </Link>
          <a
            href={`tel:${BRAND_PHONE}`}
            className="px-6 py-3 rounded-full border-2 border-primary text-primary font-medium hover:bg-primary/10"
          >
            โทร {BRAND_PHONE}
          </a>
          <Link
            to="/notary"
            className="px-6 py-3 rounded-full border font-medium hover:bg-secondary"
          >
            ดูบริการ Notary Public
          </Link>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold mb-4">ทนายความท่านอื่นในทีม</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {related.map((r) => (
            <Link
              key={r.slug}
              to="/about/team/$slug"
              params={{ slug: r.slug }}
              className="rounded-xl border p-3 hover:shadow-elegant transition-shadow text-center"
            >
              <img
                src={r.image}
                alt={r.nameTh}
                width={100}
                height={120}
                loading="lazy"
                decoding="async"
                className="w-full aspect-[4/5] object-cover rounded-lg mb-2"
              />
              <p className="text-sm font-medium">{r.nameTh}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">{r.role}</p>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
